import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SharedModule } from '@shared/modules/shared.module';
import { isButtonDisabled } from './button-state';

@Component({
	selector: 'app-button',
	imports: [SharedModule],
	templateUrl: './button.component.html',
	styleUrl: './button.component.scss',
})
export class ButtonComponent {
	@Input() label = '';
	@Input() icon?: string;
	@Input() iconPos: 'left' | 'right' = 'left';
	@Input() disabled = false;
	@Input() loading = false;
	@Input() size: 'large' | 'medium' | 'small' | 'xsmall' = 'medium';
	@Input() variant:
		| 'primary'
		| 'secondary'
		| 'tertiary'
		| 'danger'
		| 'dark'
		| 'outlined'
		| 'flat'
		| 'icon-only'
		| 'icon-only-secondary'
		| 'icon-ghost'
		| 'ghost'
		| 'link' = 'primary';
	@Input() type: 'button' | 'submit' | 'reset' = 'button';
	@Input() routerLink?: string | string[];
	@Input() color: string | null = null;
	/**
	 * Accessible name. Mandatory for icon-only buttons: the icon says nothing to
	 * a screen reader and a visual tooltip is not a name (QA INC-13). When the
	 * button has a visible label this is left alone — the label is the name.
	 */
	@Input() ariaLabel?: string;

	@Output() onClick = new EventEmitter<Event>();

	handleClick(event: Event): void {
		if (!this.disabled && !this.loading) {
			this.onClick.emit(event);
		}
	}

	get isIconOnly(): boolean {
		return !!this.icon && !this.label;
	}

	/**
	 * Name exposed to assistive tech. Falls back to the label while loading,
	 * because the visible label is blanked out for the spinner and the button
	 * would otherwise go nameless mid-request.
	 */
	get resolvedAriaLabel(): string | undefined {
		return this.ariaLabel || (this.loading ? this.label : undefined) || undefined;
	}

	get isDisabled(): boolean {
		return isButtonDisabled(this.disabled, this.loading);
	}

	getButtonClasses(): string[] {
		const classes = ['custom-button', this.size, this.variant];
		if (this.isIconOnly) {
			classes.push('icon-only');
		}
		if (this.loading) {
			classes.push('loading');
		}
		if (this.color) {
			classes.push('has-custom-color');
		}
		if (this.icon && !this.isIconOnly && this.iconPos === 'right') {
			classes.push('icon-right');
		}
		return classes;
	}
}
